import { composeBrandedTitle } from "../../EmbeddablePlaylist/Components/BrandedEmbeddablePlaylist/titleComposer";

export function safeUrl(value) {
  try {
    const url = new URL(value);
    return ["https:", "http:"].includes(url.protocol) ? url.href : "";
  } catch {
    return "";
  }
}
export function normalizeClips(clips) {
  return (Array.isArray(clips) ? clips : [])
    .filter(
      (clip) =>
        !["deleted", "inactive", "error", "pending", "pruned"].includes(
          String(clip.status).toLowerCase(),
        ) && /\.mp4(?:[?#]|$)/i.test(safeUrl(clip.url)),
    )
    .map((clip) => {
      const metadata = Array.isArray(clip.metadata) ? clip.metadata : [];
      const meta = (key) => metadata.find((m) => m?.key === key)?.value || "";
      const round = Number(clip.roundNumber || meta("CS_Round"));
      const kills = Number(meta("CS_Kill Count"));
      return {
        id: clip._id,
        url: safeUrl(clip.url),
        title: composeBrandedTitle(clip),
        thumbnail: safeUrl(
          clip.thumbnailStandardUrl || clip.mobile?.thumbnailStandardUrl,
        ),
        player: meta("PARTNER_playerName") || clip.username || "",
        avatar:
          safeUrl(meta("PARTNER_playerPhoto")) ||
          safeUrl(meta("PARTNER_avatar")),
        team: meta("PARTNER_teamName"),
        teamLogo: safeUrl(meta("PARTNER_teamLogo")),
        opponent: meta("PARTNER_opponentTeamName"),
        opponentLogo: safeUrl(meta("PARTNER_opponentTeamLogo")),
        round: Number.isFinite(round) && round > 0 ? round : null,
        views: Math.max(0, Number(clip.views) || 0),
        ace:
          kills === 5 ||
          metadata.some(
            (m) => m?.key === "CS_Situational" && /^ace$/i.test(m.value),
          ),
        fourk: kills === 4,
      };
    });
}
export async function loadPlaylist(api, options, signal) {
  if (!safeUrl(api.endpoint))
    throw new Error("Playlist service is not configured.");
  const headers = { "X-API-Key": api.apiKey || "" };
  const request = async (path) => {
    const response = await fetch(`${api.endpoint.replace(/\/$/, "")}${path}`, {
      headers,
      signal,
    });
    if (!response.ok)
      throw new Error(
        response.status === 404
          ? "Playlist not found. Choose Latest or use a current playlist ID."
          : `Playlist request failed (${response.status}).`,
      );
    return response.json();
  };
  let geo = options.geo,
    region = options.region;
  if (!geo && safeUrl(api.whereami)) {
    try {
      const response = await fetch(api.whereami, {
        signal,
        credentials: "omit",
      });
      if (response.ok) {
        const location = await response.json();
        geo = location.country;
        region = location.region;
      }
    } catch (error) {
      if (error.name === "AbortError") throw error;
    }
  }
  let playlist = options.playlist;
  if (!playlist) {
    const resolved = await request(
      "/cs/clips/playlist?limit=30&sort=score&expiry=7d",
    );
    try {
      playlist = new URL(resolved.data?.playlistUrl).searchParams.get(
        "playlist",
      );
    } catch {
      /* Validated below. */
    }
    if (!playlist)
      throw new Error("No current playlist returned by the service.");
  }
  const query = new URLSearchParams({
    name: playlist,
    platform: "HLTV",
    fmt: "iframe",
    includeAdCampaign: "1",
  });
  if (geo) query.set("geo", geo.toUpperCase());
  if (geo?.toUpperCase() === "US" && region)
    query.set("region", region.toUpperCase());
  if (options.previewCampaignId)
    query.set("previewCampaignId", options.previewCampaignId);
  const response = await request(`/playlist?${query}`);
  const clips = normalizeClips(response.data?.clips);
  if (!clips.length) throw new Error("No playable clips in this playlist.");
  const campaign = response.data?.adCampaign;
  return {
    clips,
    playlist,
    geo: geo || "",
    campaign:
      campaign && !/^allstar(?:\.gg)?$/i.test(campaign.brandName?.trim())
        ? campaign
        : null,
  };
}
